import React from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useEffect} from 'react'

import {Grid} from '@mui/material'
import 'react-toastify/dist/ReactToastify.css'
import {useSnackbar} from 'notistack'
import {apiConstants} from '../../common/constants'
import axios from 'axios'

import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import Logout from '@mui/icons-material/Logout'
import SearchIcon from '@mui/icons-material/Search'
import {login, setSelectedUserId} from '../../redux/userSlice'
import {fetchReportData} from '../../redux/actions/reportAction'
import Loader from '../../common/Loader'
import {decodeAuthToken} from '../../helpers/UtilityHelper'
import {useNavigate} from 'react-router-dom'
import {applyMerchantSelection} from '../../hooks/useMerchantSwitcher'
import {performLogout} from '../../helpers/sessionHelper'
import {
  HeaderContainer,
  IconContainer,
  StyledIcon,
  StyledTextFieldClient,
  SubTitle,
  WelcomeTitle,
  MerchantContainer,
  StyledMenuItem,
  MerchantName,
  SearchContainer,
  LogoutButton,
  EmptyStateContainer,
  ArrowIcon,
} from './StyledElements'
import _ from 'lodash'

export default function SelectLoginUser() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {enqueueSnackbar} = useSnackbar()
  const [merList, setMerList] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState('')
  const [filteredMerList, setFilteredMerList] = React.useState([])
  const dataLogin = useSelector((state) => state.user?.loginUserData)
  const {merchantOid, merchantList, roles, switchableMerchantOids} = decodeAuthToken(dataLogin.accessToken)
  // Regular merchant users granted cross-account access (not Proship admins) land here via the
  // same page, but sourced from the new switchable-merchant grant, not the admin's merchantList.
  const isSwitchMode = !roles?.includes('ROLE_PROSHIPADMIN') && Array.isArray(switchableMerchantOids) && switchableMerchantOids.length > 0
  // get assigned user list
  useEffect(() => {
    const fetchMerchantData = async () => {
      setIsLoading(true)
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('userToken'),
        },
      }
      try {
        if (isSwitchMode) {
          // Dedicated endpoint for regular merchant users' granted-merchant list — reused
          // merchant/searchCached couldn't be used here since the backend scopes that
          // endpoint's results to the caller's own home merchant only for non-admins.
          const myMerchantsUrl = apiConstants.BASE_LOCAL + apiConstants.AUTH_MY_MERCHANTS
          const {data} = await axios.get(myMerchantsUrl, config)
          const list = Array.isArray(data)
            ? data.map((m) => ({id: m.merchantOid, name: m.home ? `${m.merchantName} (Home)` : m.merchantName}))
            : []
          setMerList(list)
          setFilteredMerList(list)
          return
        }

        const merchantUrl = apiConstants.BASE_LOCAL + apiConstants.MERCHANT_SEARCH_CACHED
        const {data} = await axios.post(
          merchantUrl,
          {
            andfilter: [
              {
                field: 'isTestMerchant',
                operator: 'ne',
                value: true,
              },
              {
                field: 'status',
                operator: 'eq',
                value: 'KYC_APPROVED',
              },
              {
                field: 'is_deleted',
                operator: 'eq',
                value: '0',
              },
            ],
            offset: 0,
            limit: 2000,
            includes: 'id,name,merchantId,is_deleted',
          },
          config
        )
        const allMerchants = data?.data || []

        if (merchantList?.includes(merchantOid)) {
          setMerList(allMerchants)
          setFilteredMerList(allMerchants)
        } else {
          // remove parent id if not assigned
          const filterData = allMerchants.filter((val) => val?.id !== merchantOid)
          setMerList(filterData)
          setFilteredMerList(filterData)
        }
      } catch (error) {
        console.error('Error fetching merchant data:', error) // Log the error if the request fails
      } finally {
        setIsLoading(false)
      }
    }
    fetchMerchantData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase()
    setSearchTerm(value)
    const filtered = merList.filter((merchant) => merchant.name.toLowerCase().includes(value))
    setFilteredMerList(filtered)
  }

  // login via selected user
  const handleMerchantSelect = async (val) => {
    try {
      let config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('userToken'),
        },
      }

      // Make a request to fetch the selected merchant's data
      let merchantUrl = `${apiConstants.BASE_LOCAL}${apiConstants.MERCHANT_SEARCH_CACHED}`
      const {data} = await axios.post(
        merchantUrl,
        {
          offset: 0,
          limit: 1,
          orfilter: [],
          andfilter: [{field: 'id', operator: 'eq', value: val}],
          excludes: 'mappedOrderRule,sla',
        },
        config
      )
      let merchantData = data.data[0]
      // delete merchantData.codRemittanceConfig;
      delete merchantData.mappedOrderRule
      delete merchantData.wallet
      delete merchantData.pickup_details
      delete merchantData.bank_details
      delete merchantData.alertSubscription
      delete merchantData.gstDetails
      delete merchantData.merchantWebHooks
      delete merchantData.activeSlas
      delete merchantData.alertSubscriptionB2B

      // Call sign_in_confirmation API (fire and forget - doesn't return anything)
      const signInConfirmationUrl = `${apiConstants.BASE_LOCAL}${apiConstants.MERCHANT_SIGN_IN_CONFIRMATION}?merchant=${val}`
      axios.get(signInConfirmationUrl, config).catch(() => {}) // Fire and forget - ignore errors

      dispatch(login({...dataLogin, merchant: merchantData}))
      dispatch(setSelectedUserId(val)) // Store the selected merchant ID in Redux
      dispatch(fetchReportData(val))
    } catch (error) {
      console.error('Error selecting merchant:', error)
    }
  }

  // Switch to a granted merchant account via the switchable-merchant grant. Same mechanism as
  // the admin flow above — same JWT throughout, just re-declared per request via a header (see
  // applyMerchantSelection). No dedicated switch/validation call — same as admin, which calls
  // none either; the merchant-details fetch inside applyMerchantSelection carries the header
  // itself, so an invalid grant surfaces right there as a 403.
  const handleSwitchMerchantSelect = async (val) => {
    setIsLoading(true)
    try {
      await applyMerchantSelection(dispatch, dataLogin, val)
    } catch (error) {
      console.error('Error switching merchant:', error)
      enqueueSnackbar(error?.response?.data?.message || 'Unable to switch merchant account', {variant: 'error'})
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = () => {
    // performLogout does its own redirect once the token is revoked server-side.
    performLogout('/')
  }

  return (
    <>
      {isLoading && <Loader />}
      <HeaderContainer>
        <WelcomeTitle variant='h4'>Select Merchant</WelcomeTitle>
        <SubTitle variant='body1'>Choose the merchant account you want to access</SubTitle>
      </HeaderContainer>

      <Grid container spacing={0}>
        <Grid item xs={12}>
          <SearchContainer>
            <StyledTextFieldClient
              variant='outlined'
              placeholder='Search merchants...'
              fullWidth
              size='small'
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <IconContainer>
                    <StyledIcon>
                      <SearchIcon />
                    </StyledIcon>
                  </IconContainer>
                ),
                endAdornment: (
                  <LogoutButton
                    size='small'
                    onClick={handleLogout}
                    startIcon={<Logout />}
                    variant='text'
                  >
                    Logout
                  </LogoutButton>
                ),
              }}
            />
          </SearchContainer>
        </Grid>

        <Grid item xs={12}>
          {isLoading ? (
            <Loader />
          ) : (
            <>
              {filteredMerList?.length > 0 ? (
                <MerchantContainer>
                  {/* {filteredMerList.map((merchant, index) => ( */}
                  {_.sortBy(filteredMerList, 'name').map((merchant, index) => (
                    <StyledMenuItem key={index} onClick={() => isSwitchMode ? handleSwitchMerchantSelect(merchant.id) : handleMerchantSelect(merchant.id)}>
                      <MerchantName variant='body1'>{merchant.name}</MerchantName>
                      <ArrowIcon>
                        <KeyboardArrowRightIcon />
                      </ArrowIcon>
                    </StyledMenuItem>
                  ))}
                </MerchantContainer>
              ) : (
                <EmptyStateContainer>
                  <SearchIcon sx={{fontSize: 48, mb: 2, opacity: 0.5}} />
                  <MerchantName variant='body1'>
                    {searchTerm
                      ? 'No merchants found matching your search'
                      : 'No merchants available'}
                  </MerchantName>
                </EmptyStateContainer>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </>
  )
}
